"use client";
import { cn } from "@/lib/utils";
import {
  motion,
  SpringOptions,
  useInView,
  useSpring,
  useTransform,
} from "motion/react";
import { ElementType, useEffect, useRef, useState } from "react";

export type AnimatedNumberProps = {
  value: number;
  className?: string;
  springOptions?: SpringOptions;
  as?: ElementType;
  starting?: number;
};

export function AnimatedNumber({
  value: valueProp,
  className,
  springOptions = {
    bounce: 0,
    duration: 1000,
  },
  as = "span",
  starting = 0,
}: AnimatedNumberProps) {
  const [value, setValue] = useState(0);

  const MotionComponent = motion.create(as as keyof JSX.IntrinsicElements);
  const ref = useRef(null);
  const isInView = useInView(ref);

  const spring = useSpring(value, springOptions);

  const display = useTransform(spring, (current) =>
    Math.round(current).toLocaleString(),
  );

  const opacity = useTransform(spring, [starting, valueProp], [0.1, 1]);

  useEffect(() => {
    if (isInView && value === 0 && valueProp !== 0) {
      setValue(valueProp);
    }
  }, [isInView, valueProp, value]);

  useEffect(() => {
    spring.set(value);
  }, [spring, value]);

  return (
    <span ref={ref}>
      <MotionComponent
        style={{ opacity }}
        className={cn("tabular-nums", className)}
      >
        {display}
      </MotionComponent>
    </span>
  );
}
